import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { HotelRoomCard, useHotelRoomSelection } from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./PackageHotelRoomSelection.module.scss";
import { usePackageRoomSort } from "./usePackageRoomSort";

interface PackageHotelRoomSelectionProps extends PlatformComponentProps {
  goToNextStep: () => void;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomSelection;

/**
 * Package-specific HotelRoomSelection that re-sorts rooms by package
 * incremental change price instead of hotel tripTotal price.
 */
export const PackageHotelRoomSelection = (
  props: PackageHotelRoomSelectionProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { className, children } = props;

  const { context, handlers, text } = useHotelRoomSelection(props);

  const hotelHasPaidBreakfast = useSelector(
    HotelsShopSelectors.getHotelHasPaidBreakfast
  );

  // Re-sort the rooms by package incremental price
  const sortedRooms = usePackageRoomSort(context.rooms);

  if (context.isLoading) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      {sortedRooms.length === 0 ? (
        <div className={cn("no-available-rooms")}>
          <Icon iconName={IconNameEnum.dismiss} />
          <div className={cn("title")}>{text.noAvailableRooms.title}</div>
          <div className={cn("subtitle")}>{text.noAvailableRooms.subtitle}</div>
        </div>
      ) : (
        <div className={cn("room-container")}>
          {sortedRooms.map((room) => (
            <HotelRoomCard
              key={room.roomInfo.roomId}
              room={room}
              onRoomSelection={handlers.onRoomSelection}
              nightCount={context.nightCount}
              hotelHasPaidBreakfast={hotelHasPaidBreakfast}
            />
          ))}
        </div>
      )}
      {children}
    </div>
  );
};
